import React, { useState, useRef, useEffect } from 'react';
import { IAgentBrand, BonusRecord, RecordArrayKey, PaymentRecord } from '../../types/agent';
import { useThemeStore } from '../../state/theme';
import {
  currencyOptions,
  paymentOptions,
  bonusFrequencyOptions,
  NUMERIC_DAYS_OF_WEEK,
  allTimezones,
  DAYS_OF_WEEK,
  SLACK_ALERT_CHANNELS,
} from '../../constants';
import SelectComponent from '../common/Select';
import { Minus, X, ClipboardCopy, Plus, ChevronDown } from 'lucide-react';
import { calculateWorkingHours, getRecordDisplayStatus } from '../../utils';
import Switch from '../common/Switch';

const unitOptions = [
  { label: 'Minutes', value: 'minutes' },
  { label: 'Hours', value: 'hours' },
];

type CombinedRecord =
  | { arrayKey: 'payment'; sourceIndex: number; record: PaymentRecord }
  | { arrayKey: 'bonus'; sourceIndex: number; record: BonusRecord }
  | { arrayKey: 'otherPayments'; sourceIndex: number; record: PaymentRecord };

interface AgentBrandInfoProps {
  agentbrand: IAgentBrand;
  currentBrand: IAgentBrand;
  getBrandName: (brandId: string) => string | undefined;
  handleBreakDuration: (
    brandId: string,
    parentField: keyof IAgentBrand,
    childField: string,
    child: string,
    value: any
  ) => void;
  updateToAgentPaymentField: (
    brandId: string,
    arrayKey: 'payment' | 'otherPayments',
    recordIndex: number,
    field: 'amount' | 'currency' | 'payType',
    value: string | number
  ) => void;
  updateToClientPaymentField: (
    brandId: string,
    arrayKey: 'payment' | 'otherPayments',
    recordIndex: number,
    field: 'amount' | 'currency' | 'payType',
    value: string | number | undefined
  ) => void;
  handleBrandFieldChange: (
    brandId: string,
    field: keyof IAgentBrand,
    value: any
  ) => void;
  handleNestedBrandFieldChange: (
    brandId: string,
    parentField: keyof IAgentBrand,
    childField: string,
    value: any
  ) => void;
  addNewPaymentRecord: (brandId: string, name: string) => void;
  updatePaymentRecordDate: (
    brandId: string,
    arrayKey: RecordArrayKey,
    recordIndex: number,
    field: 'effectiveStartDate' | 'effectiveEndDate',
    value: string
  ) => void;
  deletePaymentRecord: (brandId: string, arrayKey: RecordArrayKey, recordIndex: number) => void;
  updateBonusRecordField: (
    brandId: string,
    recordIndex: number,
    field: keyof BonusRecord,
    value: string | number
  ) => void;
  updateOtherPaymentName: (brandId: string, recordIndex: number, value: string) => void;
}

const AgentBrandInfo: React.FC<AgentBrandInfoProps> = ({
  agentbrand: ab,
  currentBrand: currentBrandData,
  getBrandName,
  handleBreakDuration,
  updateToAgentPaymentField,
  updateToClientPaymentField,
  handleBrandFieldChange,
  handleNestedBrandFieldChange,
  addNewPaymentRecord,
  updatePaymentRecordDate,
  deletePaymentRecord,
  updateBonusRecordField,
  updateOtherPaymentName,
}) => {
  const { isDarkMode } = useThemeStore();

  const [showPaymentDropdown, setShowPaymentDropdown] = useState<{ [brandId: string]: boolean }>({});
  const [openDayDropdown, setOpenDayDropdown] = useState<string | null>(null);

  // Scroll to and briefly highlight a newly added record.
  const [newlyAddedKey, setNewlyAddedKey] = useState<string | null>(null);
  const recordRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!newlyAddedKey) return;
    recordRefs.current[newlyAddedKey]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    const t = setTimeout(() => setNewlyAddedKey(null), 2000);
    return () => clearTimeout(t);
  }, [newlyAddedKey]);

  const paymentCategoryOptions = [
    { value: 'Salary Payment', label: 'Salary / Service Hours' },
    { value: 'Bonus', label: 'Bonus' },
    { value: 'Others', label: 'Others' },
  ];

  const timeSlots = currentBrandData.workSchedule?.timeSlots || {};
  const formatDay = (day: string) =>
    day.charAt(0).toUpperCase() + day.slice(1, 3);

  const salaryRecords: PaymentRecord[] = Array.isArray(currentBrandData.payment)
    ? currentBrandData.payment
    : currentBrandData.payment
      ? [currentBrandData.payment as PaymentRecord]
      : [];

  const bonusRecords: BonusRecord[] = Array.isArray(currentBrandData.bonus) ? currentBrandData.bonus : [];
  const otherRecords: PaymentRecord[] = Array.isArray(currentBrandData.otherPayments) ? currentBrandData.otherPayments : [];

  const combinedRecords: CombinedRecord[] = [
    ...salaryRecords.map((r, i) => ({ arrayKey: 'payment' as const, sourceIndex: i, record: r })),
    ...bonusRecords.map((r, i) => ({ arrayKey: 'bonus' as const, sourceIndex: i, record: r })),
    ...otherRecords.map((r, i) => ({ arrayKey: 'otherPayments' as const, sourceIndex: i, record: r })),
  ];

  const updateTimeSlot = (
    day: string,
    shiftIndex: number,
    field: 'startTime' | 'endTime',
    value: string
  ) => {
    const key = formatDay(day);
    const dayShifts = timeSlots[key] || [];
    const updatedShifts = [...dayShifts];
    const currentShift = updatedShifts[shiftIndex] || {};

    const updatedShift = {
      ...currentShift,
      [field]: value,
    };

    // Calculate working hours
    if (updatedShift.startTime && updatedShift.endTime) {
      updatedShift.workingHours = calculateWorkingHours(
        updatedShift.startTime,
        updatedShift.endTime
      );
    }

    updatedShifts[shiftIndex] = updatedShift;
    handleNestedBrandFieldChange(ab.brandId, 'workSchedule', 'timeSlots', {
      ...timeSlots,
      [key]: updatedShifts,
    });
  };

  const toggleWorkingDay = (day: string) => {
    const key = formatDay(day);
    const dayShifts = timeSlots[key];
    const isWorking =
      dayShifts && Array.isArray(dayShifts) && dayShifts.length > 0;

    const updated = { ...timeSlots };

    if (isWorking) {
      delete updated[key];
    } else {
      const startTime = '09:00';
      const endTime = '17:00';
      updated[key] = [
        {
          startTime,
          endTime,
          workingHours: calculateWorkingHours(startTime, endTime),
        },
      ];
    }

    handleNestedBrandFieldChange(
      ab.brandId,
      'workSchedule',
      'timeSlots',
      updated
    );
  };

  const addShiftToDay = (day: string) => {
    const key = formatDay(day);
    const dayShifts = timeSlots[key] || [];
    const startTime = '09:00';
    const endTime = '17:00';

    handleNestedBrandFieldChange(ab.brandId, 'workSchedule', 'timeSlots', {
      ...timeSlots,
      [key]: [
        ...dayShifts,
        {
          startTime,
          endTime,
          workingHours: calculateWorkingHours(startTime, endTime),
        },
      ],
    });
  };

  const removeShiftFromDay = (day: string, shiftIndex: number) => {
    const key = formatDay(day);
    const dayShifts = timeSlots[key] || [];
    const updatedShifts = dayShifts.filter((_, index) => index !== shiftIndex);

    const updated = { ...timeSlots };
    if (updatedShifts.length === 0) {
      delete updated[key];
    } else {
      updated[key] = updatedShifts;
    }

    handleNestedBrandFieldChange(
      ab.brandId,
      'workSchedule',
      'timeSlots',
      updated
    );
  };

  const copyPreviousDay = (index: number) => {
    if (index === 0) return;

    const prevDay = DAYS_OF_WEEK[index - 1].value;
    const prevKey = formatDay(prevDay);
    const prevDayShifts = timeSlots[prevKey];

    if (
      prevDayShifts &&
      Array.isArray(prevDayShifts) &&
      prevDayShifts.length > 0
    ) {
      const currentDay = DAYS_OF_WEEK[index].value;
      const currentKey = formatDay(currentDay);

      const copiedShifts = prevDayShifts.map(({ _id, ...rest }) => rest);

      handleNestedBrandFieldChange(ab.brandId, 'workSchedule', 'timeSlots', {
        ...timeSlots,
        [currentKey]: copiedShifts,
      });
    }
  };

  const hasShift = Object.values(timeSlots).some((slots: any) => slots?.length > 0);
  const payType = currentBrandData.payment?.[0]?.toAgent?.payType;
  const isDisabled = hasShift || (currentBrandData.isUpwork && payType === 'hourly');

  return (
    <div className="space-y-8 bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4">
      <div key={ab.brandId} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 col-span-2">
            <label className="text-sm font-medium">Brand</label>
            <input
              type="text"
              disabled
              value={getBrandName(ab.brandId) || ''}
              className="p-2 rounded-md border border-gray-300 bg-gray-100"
            />
          </div>

          {/* Payment History Section */}
          <div className="col-span-2">
            <div className="flex justify-between items-center mb-3">
              <h4
                className={`text-md font-medium ${
                  isDarkMode ? 'text-green-400' : 'text-blackTen'
                }`}
              >
                Payment History
              </h4>
              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    setShowPaymentDropdown((prev) => ({
                      ...prev,
                      [ab.brandId]: !prev[ab.brandId],
                    }))
                  }
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                >
                  <Plus size={16} />
                  Add Payment
                </button>
                {showPaymentDropdown[ab.brandId] && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg z-10">
                    {paymentCategoryOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          const newKey =
                            opt.value === 'Bonus'
                              ? `bonus-${bonusRecords.length}`
                              : opt.value === 'Others'
                                ? `otherPayments-${otherRecords.length}`
                                : `payment-${salaryRecords.length}`;
                          addNewPaymentRecord(ab.brandId, opt.value);
                          setShowPaymentDropdown((prev) => ({ ...prev, [ab.brandId]: false }));
                          setNewlyAddedKey(newKey);
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {combinedRecords.length === 0 ? (
                <p className="text-sm text-gray-500">No payment records found</p>
              ) : (
                combinedRecords.map(({ arrayKey, sourceIndex, record }, displayIndex) => {
                    const status = getRecordDisplayStatus(record);
                    const isActive = status === 'active';
                    const isScheduled = status === 'scheduled';
                  const fieldCls = 'w-full p-2 text-sm rounded-md border border-gray-300 bg-white';

                    const bonusRec = record as BonusRecord;
                    const payRec = record as PaymentRecord;
                    const recordKey = `${arrayKey}-${sourceIndex}`;

                    const startD = record.effectiveStartDate ? new Date(record.effectiveStartDate) : null;
                    const endD = record.effectiveEndDate ? new Date(record.effectiveEndDate) : null;
                    const dateRangeInvalid = !!(startD && endD && endD < startD);

                    const labelMap: Record<string, string> = {
                      payment: 'Salary Payment',
                      bonus: 'Bonus',
                      otherPayments: 'Others',
                    };

                    return (
                    <div
                      key={recordKey}
                      ref={(el) => { recordRefs.current[recordKey] = el; }}
                      className={`p-4 rounded-lg border bg-gray-50 transition-colors ${
                        newlyAddedKey === recordKey
                          ? 'border-blue-500 ring-2 ring-blue-300'
                          : 'border-gray-300'
                      }`}
                    >
                        {/* Card header */}
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-600">
                              Record #{displayIndex + 1}
                            </span>
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                              {labelMap[arrayKey]}
                            </span>
                            {isActive && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-500 text-white rounded">
                                Active
                              </span>
                            )}
                            {isScheduled && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded">
                                Scheduled
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                          onClick={() => deletePaymentRecord(ab.brandId, arrayKey, sourceIndex)}
                            className="text-red-500 hover:text-red-700"
                            title="Delete record"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        {/* BONUS card */}
                        {arrayKey === 'bonus' && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-200">
                              <div>
                              <label className="text-xs text-gray-600 mb-1 block">Bonus Name</label>
                                <input
                                  type="text"
                                  value={bonusRec.name || ''}

                                onChange={(e) => updateBonusRecordField(ab.brandId, sourceIndex, 'name', e.target.value)}
                                  placeholder="Enter bonus name"
                                  className={fieldCls}
                                />
                              </div>
                              <div>
                              <label className="text-xs text-gray-600 mb-1 block">Frequency</label>
                                <select
                                  value={bonusRec.frequency || ''}

                                onChange={(e) => updateBonusRecordField(ab.brandId, sourceIndex, 'frequency', e.target.value)}
                                  className={fieldCls}
                                >
                                  <option value="">Select Frequency</option>
                                  {bonusFrequencyOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                            {bonusRec.frequency === 'specific_day_of_week' && (() => {
                                  const key = `${sourceIndex}-dow`;
                              const selected = NUMERIC_DAYS_OF_WEEK.find(d => d.value === bonusRec.dayOfWeek);
                                  return (
                                    <div className="relative">
                                  <label className="text-xs text-gray-600 mb-1 block">Day of Week</label>
                                      <button
                                        type="button"
                                    onClick={() => setOpenDayDropdown(openDayDropdown === key ? null : key)}
                                        className={`${fieldCls} text-left flex justify-between items-center`}
                                      >
                                    <span className="text-gray-700">{selected?.label || 'Select Day'}</span>
                                        <ChevronDown size={14} />
                                      </button>
                                      {openDayDropdown === key && (
                                        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-44 overflow-y-auto">
                                          {NUMERIC_DAYS_OF_WEEK.map((d) => (
                                            <button
                                              key={d.value}
                                              type="button"
                                              onClick={() => {
                                            updateBonusRecordField(ab.brandId, sourceIndex, 'dayOfWeek', d.value);
                                                setOpenDayDropdown(null);
                                              }}
                                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                            >
                                              {d.label}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                            {bonusRec.frequency === 'specific_day_of_month' && (() => {
                                  const key = `${sourceIndex}-dom`;
                                  return (
                                    <div className="relative">
                                  <label className="text-xs text-gray-600 mb-1 block">Day of Month</label>
                                      <button
                                        type="button"
                                    onClick={() => setOpenDayDropdown(openDayDropdown === key ? null : key)}
                                        className={`${fieldCls} text-left flex justify-between items-center`}
                                      >
                                    <span className="text-gray-700">{bonusRec.dayOfMonth || 'Select Date'}</span>
                                    <ChevronDown size={14} className="text-gray-400" />
                                      </button>
                                      {openDayDropdown === key && (
                                        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-44 overflow-y-auto">
                                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                            <button
                                              key={d}
                                              type="button"
                                              onClick={() => {
                                            updateBonusRecordField(ab.brandId, sourceIndex, 'dayOfMonth', d);
                                                setOpenDayDropdown(null);
                                              }}
                                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                            >
                                              {d}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                              <div>
                              <label className="text-xs text-gray-600 mb-1 block">Amount to Agent</label>
                                <input
                                type="number" min={0} step="any"
                                  value={bonusRec.amount ?? ''}

                                onChange={(e) => updateBonusRecordField(ab.brandId, sourceIndex, 'amount', parseFloat(e.target.value) || 0)}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                  placeholder="0"
                                  className={fieldCls}
                                />
                              </div>
                              <div>
                              <label className="text-xs text-gray-600 mb-1 block">Amount from Client</label>
                                <input
                                type="number" min={0} step="any"
                                  value={bonusRec.amountFromClient ?? ''}

                                onChange={(e) => updateBonusRecordField(ab.brandId, sourceIndex, 'amountFromClient', parseFloat(e.target.value) || 0)}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                  placeholder="0"
                                  className={fieldCls}
                                />
                              </div>
                              <div>
                              <label className="text-xs text-gray-600 mb-1 block">Payment Type</label>
                                <select
                                  value={bonusRec.paymentType || ''}

                                onChange={(e) => updateBonusRecordField(ab.brandId, sourceIndex, 'paymentType', e.target.value)}
                                  className={fieldCls}
                                >
                                  <option value="">Select Type</option>
                                  {paymentOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </>
                        )}

                        {/* OTHERS card — name input + toAgent/fromClient */}
                        {arrayKey === 'otherPayments' && (
                          <div className="mb-3 pb-3 border-b border-gray-200">
                          <label className="text-xs text-gray-600 mb-1 block">Payment Name</label>
                            <input
                              type="text"
                            value={payRec.name === 'Others' ? '' : payRec.name || ''}

                            onChange={(e) => updateOtherPaymentName(ab.brandId, sourceIndex, e.target.value)}
                              placeholder="e.g. Internet Allowance"
                              className={fieldCls}
                            />
                          </div>
                        )}

                        {/* SALARY & OTHERS — toAgent / fromClient */}
                      {(arrayKey === 'payment' || arrayKey === 'otherPayments') && (
                          <>
                            <div className="mb-3">
                            <h5 className="text-xs font-semibold text-gray-700 mb-2">Payment to Agent</h5>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                <label className="text-xs text-gray-600 mb-1 block">Currency</label>
                                  <select
                                    value={payRec.toAgent?.currency || ''}

                                  onChange={(e) => updateToAgentPaymentField(ab.brandId, arrayKey, sourceIndex, 'currency', e.target.value)}
                                    className={fieldCls}
                                  >
                                    <option value="">Select Currency</option>
                                  {currencyOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                <label className="text-xs text-gray-600 mb-1 block">Pay Type</label>
                                  <select
                                    value={payRec.toAgent?.payType || ''}

                                  onChange={(e) => updateToAgentPaymentField(ab.brandId, arrayKey, sourceIndex, 'payType', e.target.value)}
                                    className={fieldCls}
                                  >
                                  <option value="">Select Payment Type</option>
                                  {paymentOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                <label className="text-xs text-gray-600 mb-1 block">Amount</label>
                                  <input
                                  type="number" min={0} step="any"
                                    value={payRec.toAgent?.amount ?? ''}

                                  onChange={(e) => updateToAgentPaymentField(ab.brandId, arrayKey, sourceIndex, 'amount', e.target.value)}
                                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    placeholder="00"
                                    className={fieldCls}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="mb-3">
                            <h5 className="text-xs font-semibold text-gray-700 mb-2">Payment from Client</h5>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                <label className="text-xs text-gray-600 mb-1 block">Currency</label>
                                  <select
                                    value={payRec.fromClient?.currency || ''}

                                  onChange={(e) => updateToClientPaymentField(ab.brandId, arrayKey, sourceIndex, 'currency', e.target.value)}
                                    className={fieldCls}
                                  >
                                    <option value="">Select Currency</option>
                                  {currencyOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                <label className="text-xs text-gray-600 mb-1 block">Pay Type</label>
                                  <select
                                    value={payRec.fromClient?.payType || ''}

                                  onChange={(e) => updateToClientPaymentField(ab.brandId, arrayKey, sourceIndex, 'payType', e.target.value)}
                                    className={fieldCls}
                                  >
                                  <option value="">Select Payment Type</option>
                                  {paymentOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                <label className="text-xs text-gray-600 mb-1 block">Amount</label>
                                  <input
                                  type="number" min={0} step="any"
                                    value={payRec.fromClient?.amount ?? ''}

                                  onChange={(e) => updateToClientPaymentField(ab.brandId, arrayKey, sourceIndex, 'amount', e.target.value)}
                                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    placeholder="00"
                                    className={fieldCls}
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Dates — all record types */}
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                          <label className="text-xs font-semibold text-gray-700">Start Date</label>
                            <input
                              type="date"
                            value={record.effectiveStartDate ? new Date(record.effectiveStartDate).toISOString().split('T')[0] : ''}

                            onChange={(e) => updatePaymentRecordDate(ab.brandId, arrayKey, sourceIndex, 'effectiveStartDate', e.target.value)}
                              className={fieldCls}
                            />
                          </div>
                          <div>
                          <label className="text-xs font-semibold text-gray-700">End Date</label>
                            <input
                              type="date"
                            value={record.effectiveEndDate ? new Date(record.effectiveEndDate).toISOString().split('T')[0] : ''}

                            onChange={(e) => updatePaymentRecordDate(ab.brandId, arrayKey, sourceIndex, 'effectiveEndDate', e.target.value)}
                              placeholder="Leave empty for Present"
                              className={fieldCls}
                            />
                          </div>
                        </div>
                        {dateRangeInvalid && (
                          <p className="text-xs text-red-500 mt-1">
                            End date is before start date.
                          </p>
                        )}
                      </div>
                    );
                })
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Email</label>
            <input
              type="email"
              onChange={(e) =>
                handleBrandFieldChange(ab.brandId, 'email', e.target.value)
              }
              value={currentBrandData.email || ''}
              className="p-2 rounded-md border border-gray-300 w-full"
              placeholder="Enter email"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Onboarding Date</label>
            <input
              type="date"
              onChange={(e) =>
                handleBrandFieldChange(
                  ab.brandId,
                  'onboardingDate',
                  e.target.value
                )
              }
              value={
                currentBrandData.onboardingDate
                  ? new Date(currentBrandData.onboardingDate)
                      .toISOString()
                      .split('T')[0]
                  : ''
              }
              className="p-2 rounded-md border border-gray-300 w-full"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Timezone</label>
            <select
              onChange={(e) =>
                handleBrandFieldChange(ab.brandId, 'timezone', e.target.value)
              }
              value={currentBrandData.timezone || ''}
              className="p-2 rounded-md border border-gray-300 w-full"
            >
              <option value="">Select Timezone</option>
              {allTimezones.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              Tolerance Period (minutes)
            </label>
            <input
              type="number"
              onChange={(e) =>
                handleNestedBrandFieldChange(
                  ab.brandId,
                  'workSchedule',
                  'tolerancePeriod',
                  Number(e.target.value)
                )
              }
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              value={currentBrandData.workSchedule?.tolerancePeriod ?? currentBrandData.tolerancePeriod ?? ''}
              min="0"
              className="p-2 rounded-md border border-gray-300 w-full"
              placeholder="15"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              Overdue Session Threshold (minutes)
            </label>
            <input
              type="number"
              onChange={(e) =>
                handleBrandFieldChange(
                  ab.brandId,
                  'overdueSessionThresholdMinutes',
                  Number(e.target.value)
                )
              }
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              value={currentBrandData.overdueSessionThresholdMinutes || ''}
              min="1"
              className="p-2 rounded-md border border-gray-300 w-full"
              placeholder="15"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              Idle Time Threshold (minutes)
            </label>
            <input
              type="number"
              onChange={(e) =>
                handleBrandFieldChange(
                  ab.brandId,
                  'idleTimeThresholdMinutes',
                  Number(e.target.value)
                )
              }
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              value={currentBrandData.idleTimeThresholdMinutes || ''}
              min="1"
              className="p-2 rounded-md border border-gray-300 w-full"
              placeholder="10"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              Monthly Expected Work Hours
            </label>
            <input
              type="number"
              disabled={isDisabled}
              required={!isDisabled}
              onChange={(e) =>
                handleBrandFieldChange(
                  ab.brandId,
                  'monthlyExpectedWorkHours',
                  Number(e.target.value)
                )
              }
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              value={currentBrandData.monthlyExpectedWorkHours || ''}
              min="1"
              className="p-2 rounded-md border border-gray-300 w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              placeholder={Object.values(timeSlots).some((slots: any) => Array.isArray(slots) && slots.length > 0) ? 'Shifts are scheduled' : 'Flexi-Shift Hours (eg. 160)'}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Slack Alert Channels</label>
            <SelectComponent
              isMulti
              options={SLACK_ALERT_CHANNELS}
              value={SLACK_ALERT_CHANNELS.filter((opt) =>
                (currentBrandData.slackAlertChannels || []).includes(opt.value)
              )}
              onChange={(selected: any) =>
                handleBrandFieldChange(
                  ab.brandId,
                  'slackAlertChannels',
                  (selected as typeof SLACK_ALERT_CHANNELS).map((o) => o.value)
                )
              }
              placeholder="Select alert channels..."
            />
          </div>

        </div>

        <div className="flex items-center gap-8 mt-4">
            <div className="flex items-center justify-between flex-1">
              <div>
                <label className="text-sm font-medium">Enable Leave Feature</label>
                <p className="text-xs text-gray-500 mt-0.5">Initializes leave balance from brand policy</p>
              </div>
              <Switch
                checked={currentBrandData.enableLeaveFeature || false}
                onChange={(checked) => handleBrandFieldChange(ab.brandId, 'enableLeaveFeature', checked)}
              />
            </div>

            <div className="flex items-center justify-between flex-1">
              <label className="text-sm font-medium">Upwork Agent</label>
              <Switch
                checked={currentBrandData.isUpwork || false}
                onChange={(checked) => handleBrandFieldChange(ab.brandId, 'isUpwork', checked)}
              />
            </div>
          </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ab.isUpwork && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">
                  Upwork Contract ID
                </label>
                <input
                  onChange={(e) =>
                    handleBrandFieldChange(
                      ab.brandId,
                      'upworkContractId',
                      e.target.value
                    )
                  }
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  value={currentBrandData.upworkContractId || ''}
                  className="p-2 rounded-md border border-gray-300 w-full"
                  placeholder="Contract ID"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">
                  Upwork Freelancer ID
                </label>
                <input
                  onChange={(e) =>
                    handleBrandFieldChange(
                      ab.brandId,
                      'upworkFreelancerId',
                      e.target.value
                    )
                  }
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  value={currentBrandData.upworkFreelancerId || ''}
                  className="p-2 rounded-md border border-gray-300 w-full"
                  placeholder="Freelancer ID"
                />
              </div>
            </>
          )}
        </div>

      </div>
      <div>
        <div className="space-y-4">
          <h3 className="text-md font-semibold text-blackFourty">
            Work Schedule
          </h3>
          <div className="space-y-4">
            {DAYS_OF_WEEK.map((day, dayIndex) => {
              const formattedDay = formatDay(day.value);
              const dayShifts = timeSlots[formattedDay];
              const isWorking =
                dayShifts && Array.isArray(dayShifts) && dayShifts.length > 0;

              return (
                <div key={day.value}>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-24 text-sm font-medium text-blackFourty">
                      {day.label}
                    </div>
                    {!isWorking ? (
                      <button
                        type="button"
                        onClick={() => toggleWorkingDay(day.value)}
                        className="text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1"
                      >
                        <Plus size={16} />
                        <span>Add shift</span>
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => addShiftToDay(day.value)}
                          className="text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1"
                        >
                          <Plus size={16} />
                          <span>Add shift</span>
                        </button>
                        {dayIndex > 0 && (
                          <button
                            type="button"
                            onClick={() => copyPreviousDay(dayIndex)}
                            className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
                            title="Copy previous day"
                          >
                            <ClipboardCopy size={16} />
                            <span>Copy prev</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {isWorking && (
                    <div className="ml-28 space-y-2">
                      {dayShifts.map((shift, shiftIndex) => (
                        <div
                          key={shiftIndex}
                          className="flex items-center gap-2"
                        >
                          <span className="text-xs text-gray-500 w-12">
                            Shift {shiftIndex + 1}
                          </span>
                          <input
                            type="time"
                            value={shift.startTime || ''}
                            onChange={(e) =>
                              updateTimeSlot(
                                day.value,
                                shiftIndex,
                                'startTime',
                                e.target.value
                              )
                            }
                            className="p-1.5 border rounded w-32 text-sm"
                          />
                          <span>
                            <Minus size={16} />
                          </span>
                          <input
                            type="time"
                            value={shift.endTime || ''}
                            onChange={(e) =>
                              updateTimeSlot(
                                day.value,
                                shiftIndex,
                                'endTime',
                                e.target.value
                              )
                            }
                            className="p-1.5 border rounded w-32 text-sm"
                          />
                          <span className="text-xs text-gray-500">
                            ({shift.workingHours || 0}h)
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              removeShiftFromDay(day.value, shiftIndex)
                            }
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Leave Balance (read-only) */}
          {ab.leaveBalance?.leaveTypes &&
            Object.keys(ab.leaveBalance.leaveTypes).length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Leave Balance
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-md">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 text-left">
                        <th className="px-3 py-2 font-medium">Leave Type</th>
                        <th className="px-3 py-2 font-medium text-right">
                          Accrued (h)
                        </th>
                        <th className="px-3 py-2 font-medium text-right">
                          Consumed (h)
                        </th>
                        <th className="px-3 py-2 font-medium text-right">
                          Available (h)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(ab.leaveBalance.leaveTypes).map(
                        ([leaveType, data]) => {
                          const accruals = data.monthlyAccruals ?? [];
                          const active = accruals.filter(
                            (a) => a.status === 'active'
                          );
                          const totalAccrued = active.reduce(
                            (s, a) => s + a.accrued,
                            0
                          );
                          const totalConsumed = active.reduce(
                            (s, a) => s + a.consumed,
                            0
                          );
                          const pending = (
                            ab.leaveBalance!.pendingRequests ?? []
                          )
                            .filter(
                              (p) =>
                                p.leaveType.toLowerCase() ===
                                leaveType.toLowerCase()
                            )
                            .reduce((s, p) => s + p.hours, 0);
                          const available = parseFloat(
                            Math.max(
                              0,
                              totalAccrued - totalConsumed - pending
                            ).toFixed(2)
                          );
                          return (
                            <tr
                              key={leaveType}
                              className="border-t border-gray-100"
                            >
                              <td className="px-3 py-2 capitalize">
                                {leaveType}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {totalAccrued.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {totalConsumed.toFixed(2)}
                              </td>
                              <td
                                className={`px-3 py-2 text-right font-medium ${available <= 0 ? 'text-red-500' : 'text-green-600'}`}
                              >
                                {available.toFixed(2)}
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
                {ab.leaveBalance.pendingRequests &&
                  ab.leaveBalance.pendingRequests.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      *Available reflects{' '}
                      {ab.leaveBalance.pendingRequests.length} pending
                      request(s) deducted
                    </p>
                  )}
              </div>
            )}

          {/* Break Duration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Break Duration
              </label>
              <input
                onChange={(e) =>
                  handleBreakDuration(
                    ab.brandId,
                    'workSchedule',
                    'breakDuration',
                    'value',
                    e.target.value
                  )
                }
                value={currentBrandData?.workSchedule?.breakDuration?.value}
                type="number"
                className="w-full p-2 border rounded text-sm"
                placeholder="e.g. 30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit
              </label>
              <select
                onChange={(e) =>
                  handleBreakDuration(
                    ab.brandId,
                    'workSchedule',
                    'breakDuration',
                    'unit',
                    e.target.value
                  )
                }
                value={
                  currentBrandData?.workSchedule?.breakDuration?.unit || ''
                }
                className="p-2 rounded-md border border-gray-300 w-full"
              >
                <option value="">Select Unit</option>
                {unitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentBrandInfo;
